import { useAutoScroll } from '@/hooks/use-auto-scroll';
import { cn } from '@/lib/utils';
import type { ChatMessage, RuntimeEvent } from '@/lib/agent-types';

type MessageListProps = {
  messages: ChatMessage[];
};

// ── Tool Event Helpers ──────────────────────────────────────────

function findToolResults(events: RuntimeEvent[]) {
  const results = new Map<string, RuntimeEvent>();
  for (const e of events) {
    if (e.type === 'tool_result') {
      results.set(e.tool_use_id, e);
    }
  }
  return results;
}

function ToolBlock({ event, result }: { event: RuntimeEvent; result?: RuntimeEvent }) {
  if (event.type !== 'tool_use') return null;

  const resultEvent = result?.type === 'tool_result' ? result : undefined;

  return (
    <details className="bg-muted/50 mt-1 rounded border text-xs">
      <summary className="text-muted-foreground cursor-pointer px-2 py-1 font-mono">
        Tool: {event.name}
      </summary>
      <div className="space-y-1 border-t px-2 py-1">
        <div>
          <span className="text-muted-foreground">Input:</span>
          <pre className="mt-0.5 overflow-x-auto font-mono whitespace-pre-wrap">
            {JSON.stringify(event.input, null, 2)}
          </pre>
        </div>
        {resultEvent && (
          <div>
            <span className={cn('text-muted-foreground', resultEvent.is_error && 'text-red-500')}>
              {resultEvent.is_error ? 'Error:' : 'Result:'}
            </span>
            <pre className="mt-0.5 overflow-x-auto font-mono whitespace-pre-wrap">
              {resultEvent.content}
            </pre>
          </div>
        )}
      </div>
    </details>
  );
}

function ToolEvents({ events }: { events: RuntimeEvent[] }) {
  const toolUses = events.filter((e) => e.type === 'tool_use');
  if (toolUses.length === 0) return null;

  const resultMap = findToolResults(events);

  return (
    <div className="mt-2 space-y-1">
      {toolUses.map((e) => (
        <ToolBlock
          key={e.type === 'tool_use' ? e.id : undefined}
          event={e}
          result={e.type === 'tool_use' ? resultMap.get(e.id) : undefined}
        />
      ))}
    </div>
  );
}

// ── Message Bubble ──────────────────────────────────────────────

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  const isStreaming = message.status === 'streaming';

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-lg px-3 py-2 text-sm',
          isUser ? 'bg-primary/10' : 'bg-muted'
        )}
      >
        <div className="whitespace-pre-wrap">{message.content}</div>
        {isStreaming && (
          <span className="text-muted-foreground ml-1 inline-block animate-pulse">...</span>
        )}
        {message.events.length > 0 && <ToolEvents events={message.events} />}
      </div>
    </div>
  );
}

// ── Message List ────────────────────────────────────────────────

export function MessageList({ messages }: MessageListProps) {
  const lastMessage = messages[messages.length - 1];
  const { containerRef, handleScroll } = useAutoScroll([messages.length, lastMessage?.content]);

  if (messages.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">
        No messages yet. Send a message to start chatting.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 space-y-3 overflow-y-auto p-4"
    >
      {messages.map((msg) => (
        <MessageBubble key={msg.id} message={msg} />
      ))}
    </div>
  );
}
